import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useRouter } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { queryKeys } from "@/lib/query-keys";

import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { CardTitle, ErrorState, FieldGroup, Text } from "@/components/ui/text";
import { FormGrid } from "@/components/ui/form-grid";
import { PageNavigationHeader } from "@/components/ui/page-navigation-header";
import { Skeleton } from "@/components/ui/skeleton";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeaderCell,
  TableRow,
} from "@/components/ui/table";
import { ConfirmDialog } from "@/components/ui/confirm-dialog";
import { HStack } from "@/components/ui/h-stack";
import { TableViewport } from "@/components/ui/table-viewport";
import { useToast } from "@/components/ui/toast";
import { VStack } from "@/components/ui/v-stack";
import {
  approveAdminApplication,
  rejectAdminApplication,
  getAdminApplication,
} from "@/lib/api/admin/applications";
import { ApplicationStatusBadge } from "@/components/admin/application-status";
import { formatAdminEntityTypeLabel } from "@/components/admin/formatting";
import { openFile, downloadFile } from "@/lib/api/files";
import { fromIntlLanguage } from "@/lib/i18n";
import { formatDate } from "@/lib/formatting";
import { getLocalizedName } from "@/lib/localization";
import { TicketActivitySection } from "@/components/shared/ticket-activity-section";

function usePrefersReducedMotion() {
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = () => setPrefersReducedMotion(mediaQuery.matches);

    onChange();
    mediaQuery.addEventListener("change", onChange);
    return () => mediaQuery.removeEventListener("change", onChange);
  }, []);

  return prefersReducedMotion;
}

function RevealBlock({
  index,
  children,
}: {
  index: number;
  children: React.ReactNode;
}) {
  const prefersReducedMotion = usePrefersReducedMotion();
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const frame = window.requestAnimationFrame(() => {
      setVisible(true);
    });
    return () => window.cancelAnimationFrame(frame);
  }, []);

  return (
    <VStack
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? "translateY(0)" : "translateY(8px)",
        transitionProperty: "opacity, transform",
        transitionDuration: prefersReducedMotion ? "120ms" : "280ms",
        transitionTimingFunction: "ease-out",
        transitionDelay: prefersReducedMotion ? "0ms" : `${index * 70}ms`,
      }}
    >
      {children}
    </VStack>
  );
}

function AdminApplicationDetailsPage() {
  const router = useRouter();
  const navigate = useNavigate();
  const language = fromIntlLanguage(useTranslation().i18n.language);
  const { t: tAdminApplications } = useTranslation("admin.applications");
  const { t: tAdminApplicationDetails } = useTranslation(
    "admin.applicationDetails",
  );
  const { t: tAdminEntityType } = useTranslation(
    "admin.applications.entityType",
  );
  const { t: tCommon } = useTranslation("common");
  const params = Route.useParams();
  const id = params?.id;

  const {
    data: application = null,
    isLoading: loading,
    isError: isErrorKind,
    refetch: refetchApplication,
  } = useQuery({
    queryKey: queryKeys.admin.application(id ?? ""),
    queryFn: () => getAdminApplication(id!),
    enabled: !!id,
  });
  const queryClient = useQueryClient();
  const { addToast } = useToast();
  const [action, setAction] = useState<{
    kind: "idle" | "approving" | "rejecting" | "confirmReject";
    didMutate: boolean;
  }>({ kind: "idle", didMutate: false });

  const canApprove =
    application?.is_approved === false &&
    application?.status !== "Rejected" &&
    application?.status !== "RejectedTemporarily";

  const submitting = action.kind === "approving";
  const rejecting = action.kind === "rejecting";
  const showRejectConfirm = action.kind === "confirmReject";
  const didMutate = action.didMutate;

  async function handleApprove() {
    if (!id) return;
    try {
      setAction((p) => ({ ...p, kind: "approving" }));
      await approveAdminApplication(id);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.admin.application(id),
      });
      setAction({ kind: "idle", didMutate: true });
    } catch (error) {
      console.error("Failed to approve application", error);
      setAction((p) => ({ ...p, kind: "idle" }));
    }
  }

  async function handleReject() {
    if (!id) return;
    try {
      setAction((p) => ({ ...p, kind: "rejecting" }));
      await rejectAdminApplication(id);
      await queryClient.invalidateQueries({
        queryKey: queryKeys.admin.application(id),
      });
      setAction({ kind: "idle", didMutate: true });
      addToast(tAdminApplicationDetails("rejected"), "success");
    } catch {
      addToast(tCommon("deleteFailed"), "error");
      setAction((p) => ({ ...p, kind: "idle" }));
    }
  }

  return (
    <>
      <VStack gap="xl" data-testid="admin-application-details-page">
        <PageNavigationHeader
          contextLabel={tAdminApplications("title")}
          title={
            loading ? (
              <Skeleton className="h-7 w-64" />
            ) : (
              (application?.name ?? tAdminApplications("title"))
            )
          }
          metadata={
            application
              ? `${tAdminApplications("columns.submitted")} ${formatDate(application.created_at, language)}`
              : tCommon("loading")
          }
          status={
            application ? (
              <ApplicationStatusBadge application={application} />
            ) : (
              <Skeleton className="h-7 w-24" rounded="full" />
            )
          }
          actions={
            <>
              <Button
                type="button"
                color="success"
                disabled={!canApprove || submitting}
                loading={submitting}
                onClick={() => void handleApprove()}
                data-testid="application-approve-btn"
              >
                {tAdminApplicationDetails("approve")}
              </Button>
              <Button
                type="button"
                color="danger"
                disabled={!canApprove || rejecting}
                loading={rejecting}
                onClick={() =>
                  setAction((p) => ({ ...p, kind: "confirmReject" }))
                }
                data-testid="application-reject-btn"
              >
                {tAdminApplicationDetails("reject")}
              </Button>
            </>
          }
          onBack={() => {
            if (didMutate) {
              void navigate({
                to: "/admin/applications",
                search: { refresh: String(Date.now()) },
              });
              return;
            }
            if (typeof window !== "undefined" && window.history.length > 1) {
              router.history.back();
              return;
            }
            void navigate({ to: "/admin/applications" });
          }}
          backAriaLabel={tCommon("back")}
          contextAriaLabel={tAdminApplicationDetails("backToApplications")}
        />

        {application?.tickets ? (
          <TicketActivitySection tickets={application.tickets} />
        ) : null}

        {isErrorKind ? (
          <ErrorState
            title={tCommon("error")}
            description={tAdminApplicationDetails("failedToLoadApplication")}
            action={
              <Button
                color="neutral"
                variant="outline"
                onClick={() => void refetchApplication()}
              >
                {tCommon("retry")}
              </Button>
            }
            className="h-96"
          />
        ) : null}

        <FormGrid cols="lg" gap="xl">
          <RevealBlock index={0}>
            <Card variant="bordered" padding="none">
              <CardHeader>
                <CardTitle>
                  {tAdminApplicationDetails("companyInformation")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {loading || !application ? (
                  <VStack>
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                    <Skeleton className="h-4 w-2/3" />
                  </VStack>
                ) : (
                  <FormGrid cols="sm" gap="xl">
                    <FieldGroup
                      label={tAdminApplicationDetails("fields.companyName")}
                      value={application.name}
                      valueClassName="text-sm"
                    />
                    <FieldGroup
                      label={tAdminApplicationDetails("fields.entityType")}
                      value={formatAdminEntityTypeLabel(
                        application.entity_type,
                        tAdminEntityType,
                      )}
                      valueClassName="text-sm"
                    />
                    <FieldGroup
                      label={tAdminApplicationDetails("fields.country")}
                      value={application.country?.name ?? "—"}
                      valueClassName="text-sm"
                    />
                    <FieldGroup
                      label={tAdminApplicationDetails("fields.city")}
                      value={application.city?.name ?? "—"}
                      valueClassName="text-sm"
                    />
                    <FieldGroup
                      label={tAdminApplicationDetails("fields.address")}
                      value={application.address ?? "—"}
                      valueClassName="text-sm"
                      className="sm:col-span-2"
                    />
                    <FieldGroup
                      label={tAdminApplicationDetails("fields.phone")}
                      value={application.phone_number ?? "—"}
                      valueClassName="text-sm"
                    />
                    <FieldGroup
                      label={tAdminApplicationDetails("fields.email")}
                      value={application.email ?? "—"}
                      valueClassName="text-sm"
                    />
                  </FormGrid>
                )}
              </CardContent>
            </Card>
          </RevealBlock>

          <RevealBlock index={1}>
            <Card variant="bordered" padding="none">
              <CardHeader>
                <CardTitle>
                  {tAdminApplicationDetails("contactPerson")}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {loading || !application ? (
                  <VStack>
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </VStack>
                ) : (
                  <FormGrid cols="sm" gap="xl">
                    <FieldGroup
                      label={tAdminApplicationDetails("fields.applicant")}
                      value={application.applicant?.name ?? "—"}
                      valueClassName="text-sm"
                    />
                    <FieldGroup
                      label={tAdminApplicationDetails("fields.entity")}
                      value={application.entity?.name ?? "—"}
                      valueClassName="text-sm"
                    />
                  </FormGrid>
                )}
              </CardContent>
            </Card>
          </RevealBlock>
        </FormGrid>

        <RevealBlock index={2}>
          <Card variant="bordered" padding="none">
            <CardHeader>
              <CardTitle>{tAdminApplicationDetails("documents")}</CardTitle>
            </CardHeader>
            <CardContent>
              {loading || !application ? (
                <VStack>
                  <Skeleton className="h-4 w-full" />
                  <Skeleton className="h-4 w-5/6" />
                  <Skeleton className="h-4 w-2/3" />
                </VStack>
              ) : application.attachments.length === 0 ? (
                <Text color="muted">
                  {tAdminApplicationDetails("noDocuments")}
                </Text>
              ) : (
                <TableViewport>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableHeaderCell>
                          {tAdminApplicationDetails("documentsTable.kind")}
                        </TableHeaderCell>
                        <TableHeaderCell>
                          {tAdminApplicationDetails("documentsTable.document")}
                        </TableHeaderCell>
                        <TableHeaderCell className="text-right">
                          {tAdminApplicationDetails("documentsTable.actions")}
                        </TableHeaderCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {application.attachments.map((doc) => (
                        <TableRow key={doc.id}>
                          <TableCell>
                            {getLocalizedName(doc.kind, language)}
                          </TableCell>
                          <TableCell className="font-medium">
                            {doc.file_id}
                          </TableCell>
                          <TableCell className="text-right">
                            <HStack justify="end" gap="lg">
                              <Button
                                variant="link"
                                size="sm"
                                data-testid={`application-document-view-${doc.file_id}`}
                                onClick={() => {
                                  void openFile(doc.file_id).catch(() =>
                                    addToast(
                                      tAdminApplicationDetails(
                                        "documentsTable.openError",
                                      ),
                                      "error",
                                    ),
                                  );
                                }}
                              >
                                {tAdminApplicationDetails("actions.view")}
                              </Button>
                              <Button
                                variant="link"
                                size="sm"
                                data-testid={`application-document-download-${doc.file_id}`}
                                onClick={() => {
                                  void downloadFile(doc.file_id).catch(() =>
                                    addToast(
                                      tAdminApplicationDetails(
                                        "documentsTable.downloadError",
                                      ),
                                      "error",
                                    ),
                                  );
                                }}
                              >
                                {tAdminApplicationDetails("actions.download")}
                              </Button>
                            </HStack>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableViewport>
              )}

              <Text size="xs" color="muted" className="mt-3">
                {tAdminApplicationDetails("rejectNotAvailable")}
              </Text>
            </CardContent>
          </Card>
        </RevealBlock>
      </VStack>

      <ConfirmDialog
        open={showRejectConfirm}
        title={tAdminApplicationDetails("reject")}
        description={tAdminApplicationDetails("confirmReject")}
        confirmLabel={tAdminApplicationDetails("reject")}
        cancelLabel={tCommon("cancel")}
        confirmColor="danger"
        loading={rejecting}
        onConfirm={() => void handleReject()}
        onCancel={() => setAction((p) => ({ ...p, kind: "idle" }))}
      />
    </>
  );
}

export const Route = createFileRoute("/_app/admin/applications/$id")({
  component: AdminApplicationDetailsPage,
});
